<script setup lang="ts">
import CompletableCard from '@/components/CompletableCard.vue'
import { computed, type ComputedRef, inject, onBeforeMount, onMounted, ref } from 'vue'
import { useNow } from '@/composables/game/time'
import { usePlayer, usePlayerProfit } from '@/composables/game/player'
import TapSection from '@/components/TapSection.vue'
import router from '@/router'
import { RouteName } from '@/routes'
import { useDailyCombo, useDailyReward } from '@/composables/game/daily'
import { getNextOccurrenceDiffFromNow } from '@/helpers/date'
import { useConfig } from '@/composables/game/config'
import DailyChiperSection from '@/components/DailyCipherSection.vue'
import CoinsTotalWrapper from '@/components/CoinsTotalWrapper.vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import BuyStarterPackModal from '@/components/modals/BuyStarterPackModal.vue'
import BuyAfterStarterPackModal from '@/components/modals/BuyAfterStarterPackModal.vue'
import { useLimitedTimeOffers } from '@/composables/game/ecommerce'
import {
  ECOMMERCE_ITEM_PLACEMENT,
  ECOMMERCE_ITEM_TAG,
  type EcommerceItem,
  PATHWAY_EVENT_TYPE,
} from '@/types/shared/player-config'
import ChallengeModal from '@/components/modals/CardChallengeModal.vue'
import { useAnalytics } from '@/composables/analytics'
import { useSkin } from '@/composables/game/skin'
import BannerCarousel from '@/components/BannerCarousel.vue'
import { usePacks } from '@/composables/game/shop'
import { usePathwayEventOverlay, usePathwayEvents } from '@/composables/game/pathway-events'
import { usePuzzles, usePuzzlesOverlay } from '@/composables/game/puzzles'
import { useCardChallenge } from '@/composables/game/card-challenge'
import { useModals } from '@/composables/modal-helper'
import { useGiveaway } from '@/composables/game/giveaway'
import SkinImage from '@/assets/images/skins/mascot-s3.webp'
import { useExchange, useExchangeOverlay } from '@/composables/game/exchange'
import { type LiveOpsEventWithTemplate, useLiveOpsEvents } from '@/composables/game/live-ops-events'
import { useLiveOpsOverlay } from '@/composables/game/live-ops-overlay'

const WebApp = Telegram.WebApp
const { hasCheckedInToday, player } = usePlayer()
const { config } = useConfig()
const { nowDateMinuteTimestamp, now } = useNow()
const { dailyComboTime, isDailyComboDone, dailyComboIsAlmostOver, dailyComboRewardAmount } = useDailyCombo()
// const { dailyCipherTime, dailyCipherIsAlmostOver, isDailyCipherDone } = useDailyCipher()
const { getTodayPrize } = useDailyReward()
const { addHapticFeedback } = useHapticFeedback()
const { afterStarterPackDuration, afterStarterPack, starterPack } = useLimitedTimeOffers()
const { activeBoostersCount } = usePlayerProfit()
const { triggerEvent } = useAnalytics()
const { currentSkin } = useSkin()
const { activeLimitedPackItems, hasUnseenLimitedPacks, markLimitedPacksAsSeen } = usePacks(ECOMMERCE_ITEM_PLACEMENT.HOME, true)
const { pathwayEvents } = usePathwayEvents()
const { activePathwayEvents: activeClientDeals } = usePathwayEvents(PATHWAY_EVENT_TYPE.CLIENT_DEALS)
const {
  setSelectedPathwayEvent,
} = usePathwayEventOverlay()
const { unclaimedPuzzleLiveOps } = usePuzzles({ watch: true })
const { openPuzzlesOverlay } = usePuzzlesOverlay()
const { hasChallengeEnded } = useCardChallenge()
const {
  setShowDailyUpChallengeModal,
  setShowPathwayChallengeModal,
  setShowPuzzleChallengeModal,
  setShowPuzzlesCompletionChallengeModal,
  setShowLimitedPackModal,
} = useModals()
const { syncGiveaway, giveaway, shouldShowLuckyRushButton } = useGiveaway()
const { activeExchange } = useExchange()
const { setSelectedExchange } = useExchangeOverlay()
const { liveOpsEvents } = useLiveOpsEvents()
const { openLiveOpsOverlay } = useLiveOpsOverlay()

const coinsToShow = inject<ComputedRef<number>>('coinsToShow') as ComputedRef<number>

const showBanner = computed(() => WebApp.initDataUnsafe.user?.username !== 'Fom98')
const isTapping = ref(false)

const dailyRewardDuration = computed(() => getNextOccurrenceDiffFromNow(config.value.game.tasks.dailyCheckin.endHour, nowDateMinuteTimestamp.value))
const dailyRewardTime = computed(() => dailyRewardDuration.value.toFormat('hh:mm'))
const dailyRewardIsAlmostOver = computed(() => dailyRewardDuration.value.shiftTo('hours').hours < 1)

const showDailyCipher = ref(false)
const animateTapSection = ref(true)
const showStarterPackModal = ref(false)
const showAfterStarterPackModal = ref(false)
const showChallengeModal = ref<boolean>(false)

const starterPackPurchase = computed(() => player.value.ecommerce.oneTimePurchases[(config.value.game.ecommerce.items.find(i => i.tags.includes(ECOMMERCE_ITEM_TAG.STARTER)) as EcommerceItem).id])
const showStarterPackButton = computed(() => !!starterPack.value && !starterPackPurchase.value)
const afterStarterPackPurchase = computed(() => player.value.ecommerce.oneTimePurchases[(config.value.game.ecommerce.items.find(i => i.tags.includes(ECOMMERCE_ITEM_TAG.AFTER_STARTER)) as EcommerceItem).id])
const afterStarterPackCoolDown = computed(() => {
  if (!starterPackPurchase.value) return 1_000_000
  const currentCoolDown = now.value - starterPackPurchase.value.purchasedAt
  if (currentCoolDown <= afterStarterPackDuration.value) {
    return currentCoolDown
  }
  return 0
})
const showAfterStarterPackButton = computed(() => !!starterPackPurchase.value && !afterStarterPackPurchase.value)

const tapSectionShop = computed(() => ({
  hasBundleButton: showStarterPackButton.value || showAfterStarterPackButton.value,
  hasShopButton: true,
  showStarterPack: showStarterPackButton.value,
  shopCooldown: showAfterStarterPackButton.value ? afterStarterPackCoolDown.value : 0,
}))

function onBundleClick() {
  if (showAfterStarterPackButton.value) {
    showAfterStarterPackModal.value = true
  } else if (showStarterPackButton.value) {
    showStarterPackModal.value = true
  }
}

function onChallengeClick() {
  triggerEvent({ event: 'open_card_challenge_modal' })
  showChallengeModal.value = true
}

function onDailyUpChallengeClick() {
  triggerEvent({ event: 'open_card_challenge_modal' })
  setShowDailyUpChallengeModal(true)
}

function onLimitedPackClick(limitedPack: EcommerceItem) {
  triggerEvent({ event: `limitedPackView_${limitedPack.id}` })
  setShowLimitedPackModal(true, limitedPack)
}

function handleLiveOpsEventClick(event: LiveOpsEventWithTemplate) {
  openLiveOpsOverlay(event.id)
}

onMounted(() => {
  setTimeout(() => animateTapSection.value = false, 1000)
})

onBeforeMount(async () => {
  await syncGiveaway()
})
</script>

<template>
  <div
    class="px-0 flex-1 w-full h-full bg-transparent flex flex-col"
    :class="{'touch-none': isTapping}"
  >
    <div
      class="w-full h-full flex-1 flex flex-col items-center justify-between overflow-hidden pt-4 -mt-4"
    >
      <BannerCarousel
        v-if="showBanner"
        class="px-4 max-h-[100px] mb-4"
      />
      <div
        v-else
        class="w-full flex items-center justify-between gap-x-2 bounce-in-right px-4"
      >
        <CompletableCard
          class="flex-1"
          :completed="hasCheckedInToday"
          :title="$t('home.daily_reward.title', 'Daily Reward')"
          :time="dailyRewardTime"
          :prize="getTodayPrize()"
          :is-almost-over="dailyRewardIsAlmostOver"
          @click="addHapticFeedback('light');
                  router.push({name: RouteName.EARN})"
        />
        <!--        <CompletableCard-->
        <!--          class="flex-1"-->
        <!--          :completed="isDailyCipherDone"-->
        <!--          :title="$t('home.daily_cipher.title', 'Daily FUD')"-->
        <!--          :time="dailyCipherTime"-->
        <!--          :prize="config.game.challenges.dailyCipher.reward"-->
        <!--          :is-almost-over="dailyCipherIsAlmostOver"-->
        <!--          @click="addHapticFeedback('light');showDailyCipher = !showDailyCipher"-->
        <!--        />-->
        <CompletableCard
          class="flex-1"
          :completed="isDailyComboDone"
          :title="$t('daily_combo.title', 'Daily combo')"
          :time="dailyComboTime"
          :prize="dailyComboRewardAmount"
          :is-almost-over="dailyComboIsAlmostOver"
          @click="addHapticFeedback('light');router.push({name: RouteName.MINE})"
        />
      </div>
      <CoinsTotalWrapper
        class="z-[1] px-4"
        :coins="coinsToShow"
      />
      <DailyChiperSection
        v-if="showDailyCipher"
        @is-tapping="isTapping = $event"
        class=" px-4"
      />
      <TapSection
        v-else
        class="flex-1"
        :animate="animateTapSection"
        :shop="tapSectionShop"
        show-boost
        :show-challenge="!hasChallengeEnded"
        show-daily-up-challenge
        show-pathway-completion-challenge
        show-puzzle-challenge
        :puzzles="unclaimedPuzzleLiveOps"
        show-puzzles-completion-challenge
        show-memory-challenge
        show-minesweeper-challenge
        show-live-ops-challenge
        show-special-collectible-challenge
        show-onboarding-emoji
        show-skins
        show-mystery-box
        :limited-packs="activeLimitedPackItems"
        :has-unseen-limited-packs="hasUnseenLimitedPacks"
        @on-shop-click="markLimitedPacksAsSeen"
        :show-client-deals="activeClientDeals.length > 0"
        :active-client-deals-count="activeClientDeals.length"
        :show-lucky-rush="shouldShowLuckyRushButton"
        :pathway-events="pathwayEvents"
        :exchange="activeExchange"
        :live-ops-events="liveOpsEvents"
        :active-boosts-count="activeBoostersCount"
        :back-image="SkinImage"
        hide-inner-image
        fade-image-class="hidden"
        image-wrapper-class="rounded-none"
        image-inner-wrapper-class="rounded-none"
        @is-tapping="isTapping = $event"
        @on-bundle-click="onBundleClick"
        @on-challenge-click="onChallengeClick"
        @on-daily-up-challenge-click="onDailyUpChallengeClick"
        @on-pathway-completion-challenge-click="setShowPathwayChallengeModal(true)"
        @on-limited-pack-click="onLimitedPackClick"
        @on-pathway-event-click="setSelectedPathwayEvent($event)"
        @on-puzzles-click="openPuzzlesOverlay($event)"
        @on-puzzle-challenge-click="setShowPuzzleChallengeModal(true)"
        @on-puzzles-completion-challenge-click="setShowPuzzlesCompletionChallengeModal(true)"
        @on-exchange-click="setSelectedExchange($event)"
        @on-live-ops-event-click="handleLiveOpsEventClick"
      />
    </div>
  </div>

  <BuyStarterPackModal
    v-if="starterPack && showStarterPackModal"
    :starter-pack="starterPack"
    :show="showStarterPackModal"
    @hide="showStarterPackModal = false"
  />

  <BuyAfterStarterPackModal
    v-if="afterStarterPack"
    :after-starter-pack="afterStarterPack"
    :show="showAfterStarterPackModal"
    :cool-down="afterStarterPackCoolDown"
    @hide="showAfterStarterPackModal = false"
  />
  <ChallengeModal
    v-if="showChallengeModal"
    :show="showChallengeModal"
    @hide="showChallengeModal = false"
  />
</template>

<style lang="scss">

</style>
